// Inline model + provider picker for the chat console. Backed by
// pi-ai's model catalog (via src/lib/modelCatalog.js) — the curated
// short-list of CORS-friendly providers, with each provider's
// models ranked into Recommended / Strong / Other tiers so the
// "just pick something good" path lands on a Sonnet-4.6-class
// model by default.
//
// Provider + API key persist via PUT /v1/tenant/configuration's
// `llmProvider` block (encrypted server-side, never returned to the
// browser). Task model is a per-browser preference and stays in
// localStorage.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getChatCreds, setChatCreds } from "../lib/chatCreds.js";
import {
    MODEL_TIERS,
    defaultModelFor,
    defaultTaskModelFor,
    findCuratedProvider,
    getRankedModels,
    lookupCatalogModel,
    tierLabel,
} from "../lib/modelCatalog.js";

// Providers the backend tenant LLM proxy supports. The first three carry
// hardcoded upstream URLs; "custom" is the BYO-gateway option — the admin
// supplies a base URL + host allowlist and the proxy forwards there (host
// allowlist + private-range SSRF backstop enforced server-side, see the
// backend `llm_proxy` / `llm_custom_host`).
const SUPPORTED_PROVIDERS = ["anthropic", "openrouter", "openai", "custom"];
import {
    SERVER_REGISTRY,
    listConnections,
    onConnectionsChanged,
    onConnectionStatusChanged,
    clearConnection,
    startConnect,
    saveApiKeyConnection,
    saveServerConfig,
    isApiKeyServer,
    isConfigServer,
    connectionStatus,
} from "../lib/mcp/index.js";
import HoverTooltip from "./HoverTooltip.jsx";
import Select from "./Select.jsx";
import TabStrip from "./TabStrip.jsx";
import { initialChatTab } from "./chatModelPickerTabs.js";

// Pseudo-tier shown at the top of the list when the user has a
// previously-saved model: pins it above the catalog so they can
// re-pick it (or just confirm what's active) without scrolling.
const CURRENT_TIER = "current";

function formatCost(cost) {
    if (!cost) return null;
    const fmt = (n) => {
        if (n === 0) return "0";
        if (n >= 1) return `$${n.toFixed(2).replace(/\.?0+$/, "")}`;
        return `$${n.toFixed(3).replace(/\.?0+$/, "")}`;
    };
    return `${fmt(cost.input)} / ${fmt(cost.output)}`;
}

function formatContext(n) {
    if (!n) return null;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return String(n);
}

// Group ranked models by tier in display order. Empty tiers are
// omitted so the list doesn't end with a dangling header. When a
// `pinnedId` is supplied and matches a model in the list, that
// model is pulled out and shown in a "Current" group at the top
// — so reopening the picker surfaces the active model first.
function groupByTier(ranked, pinnedId) {
    const groups = [];
    let pinned = null;
    if (pinnedId) {
        pinned = ranked.find((r) => r.model.id === pinnedId) || null;
    }
    if (pinned) {
        groups.push({
            tier: CURRENT_TIER,
            items: [{ model: pinned.model, tier: pinned.tier, isDefault: pinned.isDefault }],
        });
    }
    const order = [MODEL_TIERS.REASONING, MODEL_TIERS.OTHER];
    for (const tier of order) {
        const items = ranked.filter((r) =>
            r.tier === tier && (!pinned || r.model.id !== pinned.model.id),
        );
        if (items.length) groups.push({ tier, items });
    }
    return groups;
}

// `flush`: inline in a framed pane (a workspace's dock), whose frame is the
// panel's outline: its tab row and search lie against it as cells and bands
// divided by hairlines, as the pane's own tab row does, rather than as boxes
// inside it.
export default function ChatModelPicker({ onClose, onSaved, inline = false, intro = null, flush = false }) {
    const [tenantId, setTenantId] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [provider, setProvider] = useState(SUPPORTED_PROVIDERS[0]);
    const [modelId, setModelId] = useState("");
    // Snapshot of the model id at popover-open time. Drives the
    // "Current" pinned row at the top of the model list and stays
    // stable for the lifetime of this picker instance — clicking a
    // different row doesn't move the pin until the picker is
    // closed and reopened.
    const [savedModelId, setSavedModelId] = useState("");
    // The provider that is currently persisted (empty until configured). A
    // provider change must carry a fresh key — the stored key belongs to the
    // previously selected provider — so commit() compares against this.
    const [savedProvider, setSavedProvider] = useState("");
    // Task model — optional cheap/fast model used by sub-agents
    // (kernel extractor, judges). Empty string = "use provider auto-
    // default", which the runtime resolves via defaultTaskModelFor.
    const [taskModelId, setTaskModelId] = useState("");
    const [taskModelExpanded, setTaskModelExpanded] = useState(false);
    // `apiKey` is the user-typed *new* value about to be saved (or
    // "" when they haven't typed anything). The backend never returns
    // the stored key, so an empty value with `hasApiKey=true` means
    // "leave the saved key untouched"; empty with `hasApiKey=false`
    // means "no key on file yet."
    const [apiKey, setApiKey] = useState("");
    const [hasApiKey, setHasApiKey] = useState(false);
    // True when this tenant is served chat on Origin's managed key
    // (no bring-your-own key configured). Chat works with no key; an admin can
    // still save a key to bring their own provider.
    const [managedDefault, setManagedDefault] = useState(false);
    const [revealKey, setRevealKey] = useState(false);
    // Custom (BYO-gateway) provider fields — only used when provider==="custom".
    // `baseUrl` is the OpenAI-compatible gateway URL; `allowlist` is a
    // newline-separated host list the backend proxy enforces (SSRF guard);
    // `authStyle` is the auth header the gateway expects ("bearer" for
    // OpenAI-style, "api-key" for Azure OpenAI).
    const [baseUrl, setBaseUrl] = useState("");
    const [allowlist, setAllowlist] = useState("");
    const [authStyle, setAuthStyle] = useState("bearer");
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Three tabs: "model" (the picker grid), "provider" (provider +
    // key form), "mcp" (remote MCP server picker). Default to the
    // model tab when creds are already wired up so the common-case
    // "swap models" flow is one click; first-time setup auto-flips
    // to provider.
    const [activeTab, setActiveTab] = useState("model");
    const searchRef = useRef(null);

    // Initial load: pull saved provider/model + has_api_key from the
    // backend. The picker never receives the actual key value — only
    // a flag, mirrored into the field placeholder below.
    useEffect(() => {
        let cancelled = false;
        getChatCreds()
            .then((creds) => {
                if (cancelled) return;
                setTenantId(creds.tenantId);
                setIsAdmin(Boolean(creds.isAdmin));
                setHasApiKey(Boolean(creds.hasApiKey));
                setManagedDefault(Boolean(creds.managedDefault));
                const initialProvider = SUPPORTED_PROVIDERS.includes(creds.provider)
                    ? creds.provider
                    : SUPPORTED_PROVIDERS[0];
                const initialModel = creds.model || defaultModelFor(initialProvider);
                setProvider(initialProvider);
                setModelId(initialModel);
                setSavedModelId(creds.model || "");
                setSavedProvider(creds.provider || "");
                setTaskModelId(creds.taskModel || "");
                setBaseUrl(creds.baseUrl || "");
                setAllowlist(creds.customHostAllowlist || "");
                setAuthStyle(creds.customAuthStyle || "bearer");
                // See initialChatTab: first-time setup opens the provider/key
                // form; managed-default tenants already have working chat, so
                // they land on the model picker rather than being forced into
                // key config.
                setActiveTab(initialChatTab(creds));
            })
            .catch((err) => {
                if (!cancelled) setError(err.message || String(err));
            });
        return () => { cancelled = true; };
    }, []);

    // Switching provider: snap the model to the new provider's
    // recommended default. Skips the snap when the user already has
    // a model that exists in the new provider's catalog (the same
    // OpenRouter id is sometimes valid across providers). Same logic
    // for the task model — clear it if the saved id isn't in the new
    // catalog, so the auto-default kicks in for the new provider.
    const onProviderChange = useCallback((next) => {
        setProvider(next);
        // Custom has no catalog grid — keep the user on the provider form,
        // where the URL / model / allowlist fields live.
        if (next === "custom") setActiveTab("provider");
        const ranked = getRankedModels(next);
        const stillValid = ranked.some((r) => r.model.id === modelId);
        if (!stillValid) {
            setModelId(defaultModelFor(next));
        }
        if (taskModelId && !ranked.some((r) => r.model.id === taskModelId)) {
            setTaskModelId("");
        }
        setSearch("");
    }, [modelId, taskModelId]);

    const ranked = useMemo(() => getRankedModels(provider), [provider]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return ranked;
        return ranked.filter(({ model }) =>
            model.id.toLowerCase().includes(q) || model.name.toLowerCase().includes(q));
    }, [ranked, search]);

    const groups = useMemo(() => groupByTier(filtered, savedModelId), [filtered, savedModelId]);

    // Effective task model for display. `taskModelId` is the user
    // override; when empty we fall back to the provider's auto-
    // default so the picker always shows what sub-agents will
    // actually use.
    const effectiveTaskModelId = taskModelId || defaultTaskModelFor(provider);
    const effectiveTaskModel = useMemo(
        () => lookupCatalogModel(provider, effectiveTaskModelId),
        [provider, effectiveTaskModelId],
    );
    const taskIsAuto = !taskModelId;
    const taskGroups = useMemo(
        () => groupByTier(ranked, effectiveTaskModelId),
        [ranked, effectiveTaskModelId],
    );

    // Persist current form values. `overrideModel` lets the model
    // row click commit a new selection without waiting for setState
    // — clicking a row should save immediately, the explicit Save
    // button only matters when only the provider or api key has
    // changed.
    const commit = useCallback(async (overrideModel) => {
        const finalModel = (overrideModel ?? modelId).trim();
        if (!tenantId) {
            setError("Sign in to an active tenant and try again.");
            return;
        }
        if (!isAdmin) {
            setError("Only tenant admins can change LLM settings. Ask an admin for help.");
            return;
        }
        if (!finalModel) {
            setError("Pick a model.");
            return;
        }
        // Custom (BYO-gateway) provider needs a base URL + at least one
        // allowed host before it can save — the backend rejects custom
        // without both; validate client-side for an immediate message.
        if (provider === "custom") {
            if (!baseUrl.trim()) {
                setError("Base URL is required for a custom provider.");
                return;
            }
            if (!allowlist.trim()) {
                setError("Add at least one allowed host for a custom provider.");
                return;
            }
        }
        // A key is required on first save AND whenever the provider changes —
        // the stored key belongs to the previously selected provider, and
        // reusing it would send one vendor's key to another's endpoint (the
        // backend rejects this too). A same-provider re-save may submit blank
        // to mean "leave the stored key alone".
        const trimmedKey = apiKey.trim();
        const providerChanged = provider !== savedProvider;
        // A managed-default tenant is served on Origin's key, so it needs no
        // bring-your-own key to save a model on its (unchanged) managed
        // provider — which is exactly the flow initialChatTab lands them in
        // (Model tab, no key). Switching to a different provider means going
        // bring-your-own, so a key is required again there.
        const managedNoKeyNeeded = managedDefault && !providerChanged;
        if ((!hasApiKey || providerChanged) && !trimmedKey && !managedNoKeyNeeded) {
            setError(
                providerChanged && hasApiKey
                    ? "Enter an API key for the newly selected provider."
                    : "API key is required for this tenant's first save.",
            );
            return;
        }

        setSaving(true);
        setError(null);
        try {
            // Field-level omit semantics: only send `api_key` when the
            // user actually typed something. Sending `""` clears the
            // stored key on the server — never what we want from this
            // picker (use the Apply path with an explicit clear-key
            // affordance if/when we add one).
            const payload = { provider, model: finalModel };
            if (trimmedKey) payload.apiKey = trimmedKey;
            if (provider === "custom") {
                payload.baseUrl = baseUrl.trim();
                payload.customHostAllowlist = allowlist.trim();
                payload.customAuthStyle = authStyle || "bearer";
            }
            await setChatCreds(payload);
            setApiKey("");
            setHasApiKey(true);
            setSavedModelId(finalModel);
            setSavedProvider(provider);
            await onSaved?.();
            onClose?.();
        } catch (err) {
            setError(err.message || String(err));
        } finally {
            setSaving(false);
        }
    }, [tenantId, isAdmin, apiKey, hasApiKey, provider, savedProvider, modelId, baseUrl, allowlist, authStyle, onSaved, onClose]);

    const onPickModel = useCallback((id) => {
        setModelId(id);
        commit(id);
    }, [commit]);

    // Task model commits independently — the user is just picking
    // which cheap model judges/extractors use, not touching the
    // tenant-shared primary/key. Empty string clears the override and
    // falls back to the provider's auto-default at runtime.
    const onPickTaskModel = useCallback(async (id) => {
        const next = id === taskModelId ? "" : id;
        setTaskModelId(next);
        try {
            await setChatCreds({ taskModel: next });
            await onSaved?.();
        } catch (err) {
            setError(err.message || String(err));
        }
    }, [taskModelId, onSaved]);

    // Custom (BYO-gateway) has no model catalog — its model is free text on
    // the provider form — so the Model tab is hidden for it.
    const showModelTab = provider !== "custom";
    const effectiveTab = !showModelTab && activeTab === "model" ? "provider" : activeTab;

    // Two surface modes. Inline takes over the chat dialog body —
    // shared between the first-run setup screen (no `onClose`, must
    // save) and the in-chat model swap (has `onClose`, dismissable
    // via the × button or the model pill toggle). The legacy popover
    // mode stays as a fallback for callers that mount this outside a
    // chat dialog.
    const rootClass = inline
        ? "flex h-full min-h-0 flex-col bg-[color:var(--bg)] tracking-normal normal-case"
        : "absolute left-0 bottom-full mb-[6px] z-[10006] w-[480px] max-w-[calc(100vw-32px)] border border-[color:var(--line-strong)] bg-[color:var(--bg)] tracking-normal normal-case shadow-lg";
    const tabScrollClass = inline
        ? "flex-1 min-h-0 overflow-y-auto"
        : "max-h-[320px] overflow-y-auto";
    const settingsTabs = [
        ...(showModelTab ? [{
            id: "model",
            label: "Model",
            active: effectiveTab === "model",
            onSelect: () => setActiveTab("model"),
        }] : []),
        {
            id: "provider",
            label: "Provider & API key",
            active: effectiveTab === "provider",
            onSelect: () => setActiveTab("provider"),
        },
        {
            id: "mcp",
            label: "MCP",
            active: effectiveTab === "mcp",
            onSelect: () => setActiveTab("mcp"),
        },
    ];

    return (
        <div
            role="dialog"
            aria-label="LLM configuration"
            onClick={(e) => e.stopPropagation()}
            className={rootClass}
        >
            {inline && intro ? (
                <div className={`border-b py-[18px] ${flush ? "border-[color:var(--line-soft)] px-[12px]" : "border-[color:var(--line)]"}`}>
                    {intro}
                </div>
            ) : null}

            {flush ? (
                <div role="tablist" aria-label="LLM configuration" data-settings-cells="" className="flex h-[30px] shrink-0 items-stretch border-b border-[color:var(--line-soft)]">
                    {settingsTabs.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={t.active}
                            onClick={t.onSelect}
                            className={`flex shrink-0 items-center border-r border-[color:var(--line-soft)] px-[10px] text-[11px] ${t.active ? "text-[color:var(--ink)] [background-image:linear-gradient(var(--green-soft),var(--green-soft))]" : "cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
                        >
                            {t.label}
                        </button>
                    ))}
                    <span className="min-w-0 flex-1" />
                    {onClose ? (
                        <HoverTooltip label="Close settings" placement="bottom">
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close settings"
                                className="flex w-[30px] shrink-0 cursor-pointer items-center justify-center border-l border-[color:var(--line-soft)] text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
                            >
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M8 3v10 M3.5 8.5 L8 13 L12.5 8.5" />
                                </svg>
                            </button>
                        </HoverTooltip>
                    ) : null}
                </div>
            ) : (
            <TabStrip
                tabs={settingsTabs}
                className="pt-[11px]"
                actions={onClose ? (
                    // Settings dismiss — a DOWN arrow (the panel rises from
                    // the bottom gear, so dismissing sends it back down).
                    // Boxed like the tab-strip's other 38px controls.
                    <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close settings"
                            title="Close settings"
                            className="inline-flex w-[30px] shrink-0 items-center justify-center text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] transition-colors cursor-pointer [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
                        >
                            <svg
                                viewBox="0 0 16 16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M8 3v10 M3.5 8.5 L8 13 L12.5 8.5" />
                            </svg>
                        </button>
                    </div>
                ) : null}
            />
            )}

            {effectiveTab === "model" && showModelTab ? (
                <>
                    <div className={flush ? "shrink-0 border-b border-[color:var(--line-soft)]" : "shrink-0 border-b border-[color:var(--line)] py-[8px]"}>
                        <input
                            ref={searchRef}
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search models…"
                            className={flush
                                ? "w-full bg-transparent px-[12px] py-[8px] text-[12px] leading-[1.3] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none"
                                : "w-full border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[6px] text-[12px] leading-[1.3] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none focus:border-[color:var(--line-strong)]"}
                        />
                    </div>
                    <div className={tabScrollClass}>
                        {groups.length === 0 ? (
                            <div className="px-[16px] py-[16px] text-center text-[11px] text-[color:var(--muted-soft)]">
                                No models match.
                            </div>
                        ) : (
                            groups.map((g) => (
                                <ModelTierGroup
                                    key={g.tier}
                                    tier={g.tier}
                                    items={g.items}
                                    selectedId={modelId}
                                    onSelect={onPickModel}
                                />
                            ))
                        )}
                    </div>
                    <TaskModelSection
                        expanded={taskModelExpanded}
                        onToggle={() => setTaskModelExpanded((v) => !v)}
                        isAuto={taskIsAuto}
                        effectiveModel={effectiveTaskModel}
                        effectiveModelId={effectiveTaskModelId}
                        groups={taskGroups}
                        onPick={onPickTaskModel}
                    />
                </>
            ) : effectiveTab === "mcp" ? (
                <div className={tabScrollClass}>
                    <McpTab tenantId={tenantId} />
                </div>
            ) : (
                <ProviderTab
                    isAdmin={isAdmin}
                    provider={provider}
                    onProviderChange={onProviderChange}
                    apiKey={apiKey}
                    onApiKeyChange={setApiKey}
                    hasApiKey={hasApiKey}
                    managedDefault={managedDefault}
                    revealKey={revealKey}
                    onToggleReveal={() => setRevealKey((v) => !v)}
                    baseUrl={baseUrl}
                    onBaseUrlChange={setBaseUrl}
                    allowlist={allowlist}
                    onAllowlistChange={setAllowlist}
                    authStyle={authStyle}
                    onAuthStyleChange={setAuthStyle}
                    model={modelId}
                    onModelChange={setModelId}
                    saving={saving}
                    onApply={() => commit()}
                />
            )}

            {error || effectiveTab === "model" ? (
                <div className={`flex shrink-0 items-center justify-between gap-[8px] border-t px-[16px] py-[8px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] ${flush ? "border-[color:var(--line-soft)]" : "border-[color:var(--line)]"}`}>
                    <span className="min-w-0 truncate">
                        {error ? (
                            <span className="text-[color:var(--red)]">{error}</span>
                        ) : (
                            "Click a model to apply"
                        )}
                    </span>
                </div>
            ) : null}
        </div>
    );
}

function ProviderTab({
    isAdmin,
    provider,
    onProviderChange,
    apiKey,
    onApiKeyChange,
    hasApiKey,
    managedDefault,
    revealKey,
    onToggleReveal,
    baseUrl,
    onBaseUrlChange,
    allowlist,
    onAllowlistChange,
    authStyle,
    onAuthStyleChange,
    model,
    onModelChange,
    saving,
    onApply,
}) {
    // The reveal toggle is only meaningful when the field has a value
    // the user can actually look at. The stored key is never returned
    // to the browser, so an empty input has nothing to reveal.
    const canReveal = apiKey.length > 0;
    const providerLabel = findCuratedProvider(provider)?.label || provider;
    if (!isAdmin) {
        return (
            <div className="grid gap-[10px] px-[16px] py-[14px] text-[12px] leading-[1.5] text-[color:var(--ink)]">
                <p className="m-0">
                    The chat LLM is configured by a tenant admin and shared across your tenant.
                </p>
                <div className="grid grid-cols-[80px_minmax(0,1fr)] gap-x-[10px] gap-y-[4px] text-[11px]">
                    <span className="uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">Provider</span>
                    <span className="text-[color:var(--ink)]">{providerLabel || "(unset)"}</span>
                    <span className="uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">API key</span>
                    <span className="text-[color:var(--ink)]">
                        {hasApiKey
                            ? "Configured"
                            : managedDefault
                              ? "Managed by Origin. No key required."
                              : "Not configured. Ask your admin."}
                    </span>
                </div>
            </div>
        );
    }
    return (
        <div className="grid grid-cols-[80px_minmax(0,1fr)] items-center gap-x-[10px] gap-y-[8px] py-[12px]">
            {managedDefault && !hasApiKey ? (
                <p className="col-span-2 m-0 border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px] text-[11px] leading-[1.5] text-[color:var(--muted)]">
                    Chat is using <span className="text-[color:var(--ink)]">Origin-managed inference</span>. No key is required. Add a provider key below to use your own account.
                </p>
            ) : null}
            <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                Provider
            </span>
            <Select
                value={provider}
                onChange={onProviderChange}
                options={SUPPORTED_PROVIDERS.map((id) => ({
                    value: id,
                    label: findCuratedProvider(id)?.label || id,
                }))}
                variant="field"
                ariaLabel="LLM provider"
            />

            {provider === "custom" ? (
                <>
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                        Base URL
                    </span>
                    <div className="grid gap-[4px]">
                        <input
                            type="url"
                            value={baseUrl}
                            onChange={(e) => onBaseUrlChange(e.target.value)}
                            placeholder="https://gateway.example.com/v1"
                            className="w-full border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[6px] font-mono text-[12px] leading-[1.3] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none focus:border-[color:var(--line-strong)]"
                        />
                        <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                            Enter the base URL, such as <code>https://gateway.example.com/v1</code>.
                            Origin posts to <code>{"{base}/chat/completions"}</code>.
                            Azure OpenAI query strings such as <code>?api-version=…</code> are preserved.
                        </span>
                    </div>

                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                        Model
                    </span>
                    <input
                        type="text"
                        value={model}
                        onChange={(e) => onModelChange(e.target.value)}
                        placeholder="model id your gateway expects"
                        className="w-full border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[6px] font-mono text-[12px] leading-[1.3] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none focus:border-[color:var(--line-strong)]"
                    />

                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                        Allowed hosts
                    </span>
                    <div className="grid gap-[4px]">
                        <textarea
                            value={allowlist}
                            onChange={(e) => onAllowlistChange(e.target.value)}
                            rows={2}
                            placeholder="gateway.example.com"
                            className="w-full resize-y border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[6px] font-mono text-[12px] leading-[1.3] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none focus:border-[color:var(--line-strong)]"
                        />
                        <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                            Enter one host per line. Origin forwards only to these hosts and rejects private or link-local addresses.
                        </span>
                    </div>

                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                        Auth header
                    </span>
                    <div className="grid gap-[4px]">
                        <Select
                            value={authStyle || "bearer"}
                            onChange={onAuthStyleChange}
                            options={[
                                { value: "bearer", label: "Authorization: Bearer (OpenAI-style)" },
                                { value: "api-key", label: "api-key (Azure OpenAI)" },
                            ]}
                            variant="field"
                            ariaLabel="Custom gateway auth header"
                        />
                        <span className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                            Choose the key authentication method. Azure OpenAI uses <code>api-key</code>. Most gateways use <code>Bearer</code>.
                        </span>
                    </div>
                </>
            ) : null}

            <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                API key
            </span>
            <div className={canReveal ? "grid gap-[6px] [grid-template-columns:minmax(0,1fr)_auto]" : ""}>
                <input
                    type={revealKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => onApiKeyChange(e.target.value)}
                    autoComplete="new-password"
                    // Backend never returns the stored key — the field
                    // starts blank with a placeholder that reflects
                    // whether one is already on file. Typing replaces;
                    // leaving blank on save keeps the existing key.
                    placeholder={hasApiKey ? "Saved. Type to replace." : "sk-…"}
                    className="w-full border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[6px] font-mono text-[12px] leading-[1.3] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none focus:border-[color:var(--line-strong)]"
                />
                {canReveal ? (
                    <button
                        type="button"
                        onClick={onToggleReveal}
                        className="border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                    >
                        {revealKey ? "Hide" : "Show"}
                    </button>
                ) : null}
            </div>

            <span />
            <div className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                    The Origin backend stores this key for your tenant. The browser receives only its configured status.
            </div>

            <span />
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={onApply}
                    disabled={saving}
                    className="border border-[color:var(--line-strong)] bg-[color:var(--ink)] px-[12px] py-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--bg)] disabled:opacity-50 cursor-pointer disabled:cursor-default"
                >
                    {saving ? "Saving…" : "Apply"}
                </button>
            </div>
        </div>
    );
}

// Visual treatment per connection state. Copy is the user-facing word;
// tone picks the color. "connecting" is picker-local (a redirect is in
// flight) and never comes from the stored record.
const MCP_STATUS_PILL = {
    connected:    { copy: "connected",     tone: "text-[color:var(--green)]" },
    connecting:   { copy: "connecting",    tone: "text-[color:var(--muted)]" },
    configured:   { copy: "needs sign-in", tone: "text-[color:var(--muted)]" },
    expired:      { copy: "expired",       tone: "text-[color:var(--red)]" },
    failed:       { copy: "failed",        tone: "text-[color:var(--red)]" },
    disconnected: null,
};

const MCP_BTN_PRIMARY = "border border-[color:var(--line-strong)] bg-[color:var(--ink)] px-[10px] py-[4px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--bg)] disabled:opacity-50 cursor-pointer disabled:cursor-default";
const MCP_BTN_QUIET = "border border-[color:var(--line)] bg-transparent px-[10px] py-[4px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer";

// The pre-redirect OAuth calls (metadata, client registration) run
// against a host the user typed. Bound them so a firewalled Glean
// backend cannot leave the row on "Redirecting..." forever.
const MCP_CONNECT_TIMEOUT_MS = 15_000;

function McpTab({ tenantId }) {
    const [connections, setConnections] = useState(() => listConnections(tenantId));
    const [busy, setBusy] = useState(null); // serverId currently redirecting to OAuth
    const [error, setError] = useState(null);
    // Which server's inline form is open (api key entry, or endpoint
    // config for servers like Glean). Only one at a time; the form
    // replaces the connect button for that row.
    const [formOpen, setFormOpen] = useState(null);
    // Aborts the in-flight pre-redirect OAuth calls (Cancel, Disconnect,
    // unmount). Null when nothing is connecting.
    const connectAbortRef = useRef(null);

    useEffect(() => {
        const refresh = () => setConnections(listConnections(tenantId));
        refresh();
        const unsubscribeChange = onConnectionsChanged(refresh);
        const unsubscribeStatus = onConnectionStatusChanged(refresh);
        return () => {
            unsubscribeChange();
            unsubscribeStatus();
        };
    }, [tenantId]);
    useEffect(() => () => connectAbortRef.current?.abort(), []);

    const recordById = new Map(connections.map((c) => [c.id, c.connection]));
    const allServers = Object.values(SERVER_REGISTRY);

    const cancelConnect = useCallback(() => {
        connectAbortRef.current?.abort();
        connectAbortRef.current = null;
        setBusy(null);
    }, []);

    const beginOAuth = useCallback(async (serverId) => {
        connectAbortRef.current?.abort();
        const controller = new AbortController();
        connectAbortRef.current = controller;
        const timer = window.setTimeout(() => controller.abort("timeout"), MCP_CONNECT_TIMEOUT_MS);
        setBusy(serverId);
        try {
            // Same-tab redirect: Linear's authorize endpoint can't be
            // iframed and a popup adds a "did the user close it" race we
            // don't need. The callback page navigates us back home.
            await startConnect(tenantId, serverId, { signal: controller.signal });
        } catch (err) {
            if (connectAbortRef.current !== controller) return; // superseded or cancelled
            const def = SERVER_REGISTRY[serverId];
            if (err?.name === "AbortError" || controller.signal.aborted) {
                setError(controller.signal.reason === "timeout"
                    ? `${def?.name ?? serverId} did not answer within ${MCP_CONNECT_TIMEOUT_MS / 1000} seconds. Check the server URL and your network, then try again.`
                    : null);
            } else {
                // startConnect already stored the same message on the row
                // as its failure reason; don't say it twice.
                const onRow = listConnections(tenantId).find((c) => c.id === serverId)?.connection?.lastError?.message;
                setError(onRow === err.message ? null : (err.message || String(err)));
            }
            setBusy(null);
        } finally {
            window.clearTimeout(timer);
            if (connectAbortRef.current === controller) connectAbortRef.current = null;
        }
    }, [tenantId]);

    const handleConnect = useCallback(async (serverId) => {
        if (!tenantId) {
            setError("Sign in to an active tenant and try again.");
            return;
        }
        setError(null);
        const def = SERVER_REGISTRY[serverId];
        if (!def) return;
        const record = recordById.get(serverId);
        // Credentials and endpoint config are entered inline; only once
        // a config server has its endpoint saved does Connect mean OAuth.
        if (isApiKeyServer(def) || (isConfigServer(def) && !record?.config)) {
            setFormOpen(serverId);
            return;
        }
        await beginOAuth(serverId);
    }, [tenantId, recordById, beginOAuth]);

    // Called by the form on submit. Throws so the form can show the
    // validation message next to the input; only once the value is
    // saved does the form close and (for OAuth servers) the sign-in
    // redirect begin.
    const handleSaveForm = useCallback(async (serverId, values) => {
        if (!tenantId) throw new Error("Sign in to an active tenant and try again.");
        const def = SERVER_REGISTRY[serverId];
        if (isApiKeyServer(def)) {
            saveApiKeyConnection(tenantId, serverId, values);
            setFormOpen(null);
            setError(null);
            return;
        }
        // Validates the URL shape synchronously; the reachability check
        // is the OAuth metadata fetch inside startConnect, which fails
        // before any redirect if the host is wrong.
        saveServerConfig(tenantId, serverId, values);
        setFormOpen(null);
        setError(null);
        await beginOAuth(serverId);
    }, [tenantId, beginOAuth]);

    const handleDisconnect = useCallback((serverId) => {
        if (busy === serverId) cancelConnect();
        clearConnection(tenantId, serverId);
        setError(null);
    }, [tenantId, busy, cancelConnect]);

    return (
        <div className="grid gap-[10px] py-[12px]">
            <p className="m-0 text-[11px] leading-[1.4] text-[color:var(--muted-soft)]">
                    Connect remote MCP servers to give their tools to the chat agent. OAuth tokens and API keys stay in this browser.
            </p>
            {allServers.map((server) => {
                const record = recordById.get(server.id) || null;
                const isBusy = busy === server.id;
                const status = isBusy ? "connecting" : connectionStatus(server, record);
                const pill = MCP_STATUS_PILL[status];
                const isApiKey = isApiKeyServer(server);
                const isConfig = isConfigServer(server);
                const isFormOpen = formOpen === server.id;
                const isConnected = status === "connected";
                // Something is saved for this row (creds, endpoint, or a
                // stale token), so a way to clear it must be offered even
                // when the connection is dead.
                const hasRecord = !!record;
                const neverConnected = status === "configured";
                const subtitle = isConfig && record?.config?.mcpUrl
                    ? record.config.mcpUrl
                    : (server.description || server.issuer);
                const detail = status === "failed" && record?.lastError?.message
                    ? record.lastError.message
                    : status === "expired"
                        ? "Your authorization has expired. Reconnect to sign in again."
                        : neverConnected
                            ? `Server URL saved. Sign in to ${server.name} to finish connecting.`
                            : null;
                // The main button for a row that has a record but isn't
                // live: OAuth servers run the sign-in; api-key servers
                // re-open the key form.
                const resume = () => (isApiKey ? setFormOpen(server.id) : beginOAuth(server.id));
                const resumeLabel = neverConnected ? "Sign in" : isApiKey ? "Update key" : "Reconnect";
                return (
                    <div
                        key={server.id}
                        aria-busy={isBusy || undefined}
                        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-[10px] gap-y-[8px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[8px]"
                    >
                        <div className="min-w-0">
                            <div className="flex items-center gap-[6px] text-[12px] leading-[1.2] text-[color:var(--ink)]">
                                <span className="font-medium">{server.name}</span>
                                {pill ? (
                                    <span className={`text-[10px] uppercase tracking-[0.14em] ${pill.tone}`}>
                                        {pill.copy}
                                    </span>
                                ) : isApiKey ? (
                                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                                        api key
                                    </span>
                                ) : null}
                            </div>
                            <div className="mt-[2px] truncate text-[10px] leading-[1.3] text-[color:var(--muted-soft)]" title={subtitle}>
                                {subtitle}
                            </div>
                            {detail ? (
                                <div className={`mt-[2px] text-[10px] leading-[1.3] ${status === "failed" || status === "expired" ? "text-[color:var(--red)]" : "text-[color:var(--muted)]"}`}>
                                    {detail}
                                </div>
                            ) : null}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-[6px]">
                            {isFormOpen ? (
                                <button type="button" onClick={() => setFormOpen(null)} className={MCP_BTN_QUIET}>
                                    Cancel
                                </button>
                            ) : isBusy ? (
                                <>
                                    <button type="button" onClick={cancelConnect} className={MCP_BTN_QUIET}>
                                        Cancel
                                    </button>
                                    <button type="button" disabled className={MCP_BTN_PRIMARY}>
                                        Redirecting...
                                    </button>
                                </>
                            ) : isConnected ? (
                                <>
                                    {isConfig ? (
                                        <button type="button" onClick={() => setFormOpen(server.id)} className={MCP_BTN_QUIET}>
                                            Edit URL
                                        </button>
                                    ) : null}
                                    <button type="button" onClick={() => handleDisconnect(server.id)} className={MCP_BTN_QUIET}>
                                        Disconnect
                                    </button>
                                </>
                            ) : hasRecord ? (
                                <>
                                    {isConfig ? (
                                        <button type="button" onClick={() => setFormOpen(server.id)} className={MCP_BTN_QUIET}>
                                            Edit URL
                                        </button>
                                    ) : null}
                                    <button type="button" onClick={() => handleDisconnect(server.id)} className={MCP_BTN_QUIET}>
                                        {neverConnected ? "Remove" : "Disconnect"}
                                    </button>
                                    <button type="button" onClick={resume} className={MCP_BTN_PRIMARY}>
                                        {resumeLabel}
                                    </button>
                                </>
                            ) : (
                                <button type="button" onClick={() => handleConnect(server.id)} className={MCP_BTN_PRIMARY}>
                                    Connect
                                </button>
                            )}
                        </div>
                        {isFormOpen ? (
                            <div className="col-span-2">
                                <McpEntryForm
                                    server={server}
                                    initialValues={isConfig ? record?.config : null}
                                    onSave={(values) => handleSaveForm(server.id, values)}
                                    onCancel={() => setFormOpen(null)}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            })}
            {error ? (
                <div role="alert" className="text-[11px] leading-[1.4] text-[color:var(--red)]">
                    {error}
                </div>
            ) : null}
            <div className="text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                    Tools refresh with your next message. Reopen this dialog to check the server status.
            </div>
        </div>
    );
}

// Pinned to the bottom of the picker as a `shrink-0` sibling of the
// scrollable model list. Collapsed by default — just the header bar
// shows the current effective task model (override or auto-default).
// Expanded gets its own bounded scroll area so it doesn't push the
// main list out of view: header stays visible, list scrolls
// internally up to ~40% of the viewport. Clicking the currently-
// selected row toggles back to empty (auto).
function TaskModelSection({
    expanded,
    onToggle,
    isAuto,
    effectiveModel,
    effectiveModelId,
    groups,
    onPick,
}) {
    const label = effectiveModel?.name || effectiveModelId || "(no default available)";
    return (
        <div className="shrink-0 border-t border-[color:var(--line-strong)] bg-[color:var(--bg)]">
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-center justify-between gap-[8px] bg-[color:var(--control-bg)] px-[16px] py-[8px] text-left text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
            >
                <span className="flex min-w-0 items-center gap-[8px]">
                    <span>{expanded ? "▾" : "▸"}</span>
                    <span>Task model</span>
                    <span className="min-w-0 truncate normal-case tracking-normal text-[color:var(--muted-soft)]">
                        · {label}
                        {isAuto ? (
                            <span className="ml-[6px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">auto</span>
                        ) : null}
                    </span>
                </span>
                <span className="shrink-0 normal-case tracking-normal text-[color:var(--muted-soft)]">
                    judges and extractors
                </span>
            </button>
            {expanded ? (
                <div className="max-h-[40vh] overflow-y-auto border-t border-[color:var(--line)]">
                    <div className="px-[16px] py-[6px] text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">
                        Kernel extraction and judging use this model. Automatic selection favors lower-cost options. Click a model to select it; click it again to restore automatic selection.
                    </div>
                    {groups.length === 0 ? (
                        <div className="px-[16px] py-[12px] text-center text-[11px] text-[color:var(--muted-soft)]">
                            No models in this provider's catalog.
                        </div>
                    ) : (
                        groups.map((g) => (
                            <ModelTierGroup
                                key={g.tier}
                                tier={g.tier}
                                items={g.items}
                                selectedId={effectiveModelId}
                                onSelect={onPick}
                            />
                        ))
                    )}
                </div>
            ) : null}
        </div>
    );
}

function ModelTierGroup({ tier, items, selectedId, onSelect }) {
    const headerLabel = tier === CURRENT_TIER ? "Current" : tierLabel(tier);
    return (
        <div>
            <div className="sticky top-0 z-[1] flex items-center gap-[8px] border-b border-[color:var(--line)] bg-[color:var(--bg)] px-[16px] py-[4px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                <span>{headerLabel}</span>
                {tier !== CURRENT_TIER ? (
                    <span className="text-[color:var(--muted-soft)]">· {items.length}</span>
                ) : null}
            </div>
            {items.map(({ model, isDefault }) => (
                <ModelRow
                    key={model.id}
                    model={model}
                    isDefault={isDefault}
                    selected={model.id === selectedId}
                    onSelect={() => onSelect(model.id)}
                />
            ))}
        </div>
    );
}

// The star marks the provider's configured default, not a curated tier.
function ModelRow({ model, isDefault, selected, onSelect }) {
    const cost = formatCost(model.cost);
    const ctx = formatContext(model.contextWindow);
    const vision = model.input?.includes("image");
    const reasoning = model.reasoning;
    // Selected rows get the dashboard's standard active treatment:
    // green-soft background, inset left accent bar, and bolder name.
    // The accent bar uses a pseudo-element so the row's grid layout
    // (and per-cell baselines) stays untouched.
    const selectedCls = "relative bg-[color:var(--green-soft)] text-[color:var(--ink)] before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-[color:var(--green)]";
    const idleCls = "text-[color:var(--ink)] hover:bg-[color:var(--control-bg)]";
    // Metadata cells live in fixed-width slots so they form proper
    // columns down the list — otherwise "1M" vs "1.1M" or "$3 / $15"
    // vs "$0.2 / $0.5" jitter and ragged the right edge. Each slot
    // is right-aligned within its width.
    const meta = "shrink-0 text-right text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]";
    return (
        <button
            type="button"
            onClick={onSelect}
            aria-pressed={selected}
            className={`grid w-full grid-cols-[14px_minmax(0,1fr)_auto] items-center gap-x-[8px] border-b border-[color:var(--line)] px-[16px] py-[8px] text-left text-[12px] leading-[1.35] cursor-pointer ${selected ? selectedCls : idleCls}`}
        >
            <span aria-hidden="true" className="text-center text-[color:var(--ink)]">
                {isDefault ? "★" : ""}
            </span>
            <span className="min-w-0">
                <span className={`block truncate ${selected ? "font-medium" : ""}`}>{model.name}</span>
                <span className="block truncate font-mono text-[10px] text-[color:var(--muted-soft)]">
                    {model.id}
                </span>
            </span>
            <span className="flex items-center gap-[10px]">
                <span className={`${meta} w-[36px]`} title={ctx ? `${model.contextWindow.toLocaleString()} tokens` : undefined}>
                    {ctx || ""}
                </span>
                <span className={`${meta} w-[76px]`} title={cost ? "$/M tokens input/output" : undefined}>
                    {cost || ""}
                </span>
                <span className={`${meta} w-[24px]`} title={reasoning ? "Reasoning model" : undefined}>
                    {reasoning ? "RSN" : ""}
                </span>
                <span className={`${meta} w-[24px]`} title={vision ? "Vision input supported" : undefined}>
                    {vision ? "IMG" : ""}
                </span>
            </span>
        </button>
    );
}

// Inline entry form for MCP servers that need something typed before
// they can connect: an api key (secret, masked) or an endpoint such as
// Glean's per-customer server URL (plain text, prefilled when editing).
// One input per declared field, save-on-submit. `onSave` may throw a
// validation message; it renders under the fields, next to what the
// user typed. Sits below the row so the layout flow stays predictable
// on a narrow picker.
function McpEntryForm({ server, initialValues, onSave, onCancel }) {
    const secret = isApiKeyServer(server);
    const fields = (secret ? server.credentialFields : server.configFields) || [];
    const [values, setValues] = useState(() => {
        const init = {};
        for (const f of fields) init[f.key] = initialValues?.[f.key] || "";
        return init;
    });
    const [localError, setLocalError] = useState(null);
    const [saving, setSaving] = useState(false);
    const onSubmit = async (e) => {
        e.preventDefault();
        for (const f of fields) {
            if (!values[f.key]?.trim()) {
                setLocalError(`${f.label || f.key} is required`);
                return;
            }
        }
        setLocalError(null);
        setSaving(true);
        try {
            await onSave?.(values);
        } catch (err) {
            setLocalError(err?.message || String(err));
        } finally {
            setSaving(false);
        }
    };
    return (
        <form
            onSubmit={onSubmit}
            className="mt-[6px] grid gap-[6px] border-t border-[color:var(--line)] pt-[8px]"
        >
            {fields.map((f, i) => (
                <label key={f.key} className="grid gap-[2px]">
                    <span className="flex items-baseline justify-between gap-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                        <span>{f.label || f.key}</span>
                        {f.helpUrl ? (
                            <a
                                href={f.helpUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="normal-case tracking-normal text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                            >
                                {f.helpLabel || "where to find"} &rarr;
                            </a>
                        ) : null}
                    </span>
                    <input
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- the form replaces the button the user just pressed
                        autoFocus={i === 0}
                        type={secret ? "password" : "text"}
                        inputMode={secret ? undefined : "url"}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={f.placeholder || ""}
                        value={values[f.key]}
                        onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                        className="border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[4px] text-[12px] leading-[1.2] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
                    />
                </label>
            ))}
            {localError ? (
                <div role="alert" className="text-[11px] leading-[1.4] text-[color:var(--red,#c0392b)]">
                    {localError}
                </div>
            ) : null}
            <div className="flex justify-end gap-[6px]">
                <button type="button" onClick={onCancel} className={MCP_BTN_QUIET}>
                    Cancel
                </button>
                <button type="submit" disabled={saving} className={MCP_BTN_PRIMARY}>
                    {secret ? "Save" : "Save and sign in"}
                </button>
            </div>
        </form>
    );
}
